import type { ReactNode } from "react";
import type { ModelRelease } from "../types/model";
import { getCuratedModelTags } from "../data/curated-models";
import { useI18n } from "../hooks/use-i18n";
import { useTagRowOverflow } from "../hooks/use-tag-row-overflow";
import {
  type ReleaseFilters,
  type ReleaseTagFilter,
  type ReleaseTagFilterInteraction,
  type ReleaseTagFilterKey
} from "../lib/query-models";
import { gsap, useGSAP } from "../lib/gsap";

interface ReleasePillsProps {
  record: ModelRelease;
  scrollable?: boolean;
  interactive?: boolean;
  filters?: ReleaseFilters;
  onToggleFilter?: (
    key: ReleaseTagFilterKey,
    value: string,
    interaction?: ReleaseTagFilterInteraction
  ) => void;
}

interface PillDefinition {
  id: string;
  className: string;
  label: string;
  filter: ReleaseTagFilter;
  title?: string;
  children?: ReactNode;
}

export function ReleasePills({
  record,
  scrollable = false,
  interactive = false,
  filters,
  onToggleFilter
}: ReleasePillsProps) {
  const { language, localize, t } = useI18n();
  const rowRef = useTagRowOverflow(language, scrollable);
  const { contextSafe } = useGSAP({ scope: rowRef });
  const curatedTags = getCuratedModelTags(record.id);
  // Only the pressed-tag feedback belongs to this card's context. The filter
  // change itself must stay outside it: it unmounts cards, and every animation
  // built while a contextSafe handler runs is reverted with the card.
  const pressTag = contextSafe((target: HTMLButtonElement) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.killTweensOf(target);
    gsap.fromTo(
      target,
      { scale: 0.92 },
      {
        scale: 1,
        duration: 0.32,
        ease: "back.out(2.4)",
        overwrite: "auto",
        clearProps: "transform"
      }
    );
  });
  const activateFilter = (
    target: HTMLButtonElement,
    filter: ReleaseTagFilter
  ) => {
    pressTag(target);
    onToggleFilter?.(filter.key, filter.value, {
      originModelId: record.id,
      source: "release-tag"
    });
  };
  const renderPill = ({
    id,
    className,
    label,
    filter,
    title,
    children = label
  }: PillDefinition) => {
    const active = Boolean(
      filters && (filters[filter.key] as readonly string[]).includes(filter.value)
    );
    if (!interactive || !filters || !onToggleFilter) {
      return (
        <span className={className} key={id} title={title}>
          {children}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={`${className} release-pill-button`}
        key={id}
        title={title}
        aria-label={`${t(active ? "removeFilter" : "filterBy")} ${label}`}
        aria-pressed={active}
        data-filter-key={filter.key}
        data-filter-value={filter.value}
        onClick={(event) => {
          event.stopPropagation();
          activateFilter(event.currentTarget, filter);
        }}
      >
        {children}
      </button>
    );
  };

  return (
    <span
      ref={rowRef}
      className={[
        "release-pills",
        scrollable ? "release-pills--scrollable card-tag-row" : "",
        interactive ? "release-pills--interactive" : ""
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {curatedTags.map((tag) =>
        renderPill({
          id: `curated:${tag}`,
          className: `pill pill--curated pill--${tag}`,
          label: t(tag),
          filter: { key: "curatedTags", value: tag },
          children: <span>{t(tag)}</span>
        })
      )}
      {renderPill({
        id: "category",
        className: "pill pill--category",
        label: localize(record.mainCategory, "category"),
        filter: { key: "categories", value: record.mainCategory },
        children: <span>{localize(record.mainCategory, "category")}</span>
      })}
      {renderPill({
        id: "status",
        className: `pill pill--status pill--status-${record.release.status.toLowerCase()}`,
        label: localize(record.release.status, "status"),
        title: localize(record.release.rawStatus, "status"),
        filter: { key: "statuses", value: record.release.status },
        children: <span>{localize(record.release.status, "status")}</span>
      })}
      {renderPill({
        id: "openness",
        className: `pill pill--openness pill--${record.release.openness
          .toLowerCase()
          .replaceAll(" ", "-")}`,
        label: localize(record.release.openness, "openness"),
        filter: { key: "openness", value: record.release.openness },
        children: (
          <>
            <i aria-hidden="true" />
            <span>{localize(record.release.openness, "openness")}</span>
          </>
        )
      })}
    </span>
  );
}
